import React, { useState } from 'react';
import { ProjectStatus, Country } from '../types';
import type { Project } from '../types';
import { generateProjectAnalysis } from '../services/geminiService';
import { Edit, Trash2, MapPin, Activity, Truck, Loader2, Sparkles, Eye, HelpCircle, Globe } from 'lucide-react';

interface ProjectCardProps {
  project: Project;
  onEdit: (project: Project) => void;
  onDelete: (id: string) => void;
  onView: (project: Project) => void;
  canDelete?: boolean;
  canEdit?: boolean;
  showCountry?: boolean;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onEdit, onDelete, onView, canDelete = false, canEdit = false, showCountry = false }) => {
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [confirmAnalysis, setConfirmAnalysis] = useState(false);

  const handleAnalyzeClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isLoadingAnalysis) return;

    // First click: Ask for confirmation
    if (!confirmAnalysis) {
      setConfirmAnalysis(true);
      // Auto-reset after 3 seconds if not confirmed
      setTimeout(() => setConfirmAnalysis(false), 3000);
      return;
    }

    // Second click: Execute
    setConfirmAnalysis(false);
    setIsLoadingAnalysis(true);
    try {
        const result = await generateProjectAnalysis(project);
        setAnalysis(result.summary);
    } catch (e) {
        setAnalysis("Diagnostic error: AI Engineering Core unresponsive.");
    } finally {
        setIsLoadingAnalysis(false);
    }
  };

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.COMPLETED: return 'bg-green-100 text-green-800 border-green-200';
      case ProjectStatus.IN_PROGRESS: return 'bg-blue-100 text-blue-800 border-blue-200';
      case ProjectStatus.PLANNING: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case ProjectStatus.UNDER_TENDERING: return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case ProjectStatus.PAUSED: return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCountryColor = (country?: Country) => {
    switch(country) {
      case Country.TANZANIA: return 'bg-emerald-500';
      case Country.UGANDA: return 'bg-amber-500';
      case Country.KENYA: return 'bg-red-600';
      default: return 'bg-slate-400';
    }
  };

  return (
    <div className="bg-white rounded-[32px] shadow-sm border border-gray-200 overflow-hidden hover:shadow-xl hover:border-indigo-200 transition-all duration-300 flex flex-col h-full group relative">
      
      {/* Card Header & Content */}
      <div className="p-8 flex-grow cursor-pointer relative" onClick={() => onView(project)}>
        
        {/* Status Badge */}
        <div className="flex justify-between items-start mb-6">
           <div className="flex flex-col gap-2">
               <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusColor(project.status)} shadow-sm`}>
                  {project.status}
                </span>
                {showCountry && (
                    <div className="flex items-center gap-2 ml-1">
                        <div className={`w-1.5 h-1.5 rounded-full ${getCountryColor(project.country)} animate-pulse`}></div>
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">{project.country} NODE</span>
                    </div>
                )}
           </div>
            
            {/* Actions (Visible on Hover/Mobile) */}
             <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-all absolute top-6 right-6 bg-white/95 p-1.5 rounded-2xl shadow-xl border border-gray-100 backdrop-blur-sm">
                {canEdit && (
                    <button 
                        onClick={(e) => { e.stopPropagation(); onEdit(project); }} 
                        className="text-gray-400 hover:text-indigo-600 transition-colors p-2.5 hover:bg-indigo-50 rounded-xl"
                        title="Modify Profile"
                    >
                        <Edit size={18} />
                    </button>
                )}
                {canDelete && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDelete(project.id); }} 
                    className="text-gray-400 hover:text-rose-600 transition-colors p-2.5 hover:bg-rose-50 rounded-xl"
                    title="Revoke Registry"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
             </div>
        </div>

        <h3 className="text-xl font-black text-slate-900 leading-tight group-hover:text-indigo-600 transition-colors mb-6 line-clamp-2 min-h-[3rem] uppercase tracking-tight">
            {project.name}
        </h3>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-4 mb-8 bg-slate-50/50 p-5 rounded-[2rem] border border-slate-100 shadow-inner">
          <div className="space-y-1">
            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Region</span>
            <div className="flex items-center text-xs font-bold text-slate-700">
                <MapPin size={12} className="mr-1.5 text-indigo-400 flex-shrink-0" />
                <span className="truncate">{project.region}</span>
            </div>
          </div>
          <div className="space-y-1">
            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Distance</span>
            <div className="flex items-center text-xs font-black text-slate-900">
                <Activity size={12} className="mr-1.5 text-indigo-400 flex-shrink-0" />
                <span>{project.kilometers} km</span>
            </div>
          </div>
          <div className="col-span-2 space-y-1 mt-1 border-t border-slate-200/50 pt-3">
            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Lead Contractor</span>
            <div className="flex items-center text-xs font-bold text-slate-700">
                <Truck size={12} className="mr-1.5 text-indigo-300 flex-shrink-0" />
                <span className="truncate uppercase">{project.contractor}</span>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-2">
          <div className="flex justify-between text-[10px] mb-2 font-black uppercase tracking-widest">
            <span className="text-slate-400">Execution Progress</span>
            <span className={project.progress === 100 ? 'text-emerald-600' : 'text-indigo-600'}>{project.progress}%</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden shadow-inner">
            <div 
              className={`h-full rounded-full transition-all duration-1000 ease-out shadow-sm ${
                project.progress === 100 ? 'bg-emerald-500' : 'bg-indigo-600'
              }`} 
              style={{ width: `${project.progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Footer / AI Action */}
      <div className="p-6 bg-slate-50 border-t border-slate-100 rounded-b-[32px]">
        {!analysis ? (
           <div className="flex gap-3">
              <button 
                onClick={(e) => { e.stopPropagation(); onView(project); }} 
                className="flex-1 flex items-center justify-center px-4 py-3 bg-white border border-slate-200 shadow-sm text-[10px] font-black uppercase tracking-widest rounded-2xl text-slate-600 hover:bg-slate-50 hover:border-indigo-300 transition-all active:scale-95"
              >
                <Eye size={16} className="mr-2 text-slate-400" /> Audit Registry
              </button>
              <button 
                onClick={handleAnalyzeClick}
                onMouseLeave={() => confirmAnalysis && setConfirmAnalysis(false)}
                disabled={isLoadingAnalysis}
                className={`flex-1 flex items-center justify-center px-4 py-3 border shadow-sm text-[10px] font-black uppercase tracking-widest rounded-2xl transition-all duration-200 active:scale-95 ${
                  confirmAnalysis 
                    ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700 shadow-purple-200' 
                    : 'text-purple-700 bg-white border-purple-100 hover:bg-purple-50 shadow-sm'
                }`}
              >
                {isLoadingAnalysis ? (
                  <><Loader2 size={16} className="animate-spin mr-2" /> Syncing...</>
                ) : confirmAnalysis ? (
                  <><HelpCircle size={16} className="mr-2" /> Verify?</>
                ) : (
                  <><Sparkles size={16} className="mr-2" /> AI Check</>
                )}
              </button>
           </div>
        ) : (
          <div className="text-sm text-gray-700 animate-in fade-in slide-in-from-bottom-2 bg-white p-5 rounded-2xl border border-purple-100 shadow-lg">
            <div className="flex justify-between items-center mb-3">
                <h4 className="font-black text-purple-800 flex items-center text-[10px] uppercase tracking-widest">
                    <Sparkles size={14} className="mr-1.5"/> Intelligent Profile
                </h4>
                <button onClick={(e) => { e.stopPropagation(); setAnalysis(null); }} className="p-1 text-gray-300 hover:text-gray-600 bg-slate-50 rounded-lg"><X size={14} /></button>
            </div>
            <div className="prose prose-sm max-w-none text-xs whitespace-pre-line leading-relaxed text-slate-600 max-h-32 overflow-y-auto custom-scrollbar font-medium italic">
              {analysis}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

import { X } from 'lucide-react';
export default ProjectCard;